import {
  Input,
  Typography,
  Button,
  Controller,
  Delimiter,
  TextEditor,
  useForm,
  FileElement,
  BottomNavigation,
  FormDirtyModal,
  PageModal,
} from '@otp/ui';
import { useTranslation } from 'react-i18next';
import {
  CreateWttpQuestionDocument,
  UpdateWttpQuestionDocument,
  WttpQuestion,
  WttpQuestionImportance,
} from 'src/graphql/generated/graphql';
import 'react-quill/dist/quill.snow.css';

import { AttachmentsField } from 'src/components/common/resources/form/attachments-field';
import styles from './index.module.css';
import { CategoriesField } from 'src/components/wttp/questions/form/fields/categories';
import { ImportanceField } from 'src/components/wttp/questions/form/fields/importance';
import { DocumentNode, useMutation } from '@apollo/client';
import {
  mapFileAssetsToForm,
  mapFileAssetsToRequest,
} from 'src/components/common/resources/form/utils';
import { useEffect, useState } from 'react';

type Form = {
  title: string;
  description: string;
  questionCategoryId: string;
  importance: WttpQuestionImportance;
  isActive: boolean;
  fileAssets: FileElement[];
  parentId?: string;
};

interface QuestionFormProps {
  onFinish?: () => void;
  onClose?: () => void;
  question?: Partial<WttpQuestion>;
  refetchQueries?: DocumentNode[];
  isClone?: boolean;
  title?:
    | string
    | (string &
        React.ReactElement<any, string | React.JSXElementConstructor<any>>)
    | (string & Iterable<React.ReactNode>)
    | (string & React.ReactPortal);
}

const getFormData = (question?: Partial<WttpQuestion>) => ({
  title: question?.title || '',
  description: question?.description || undefined,
  questionCategoryId: question?.questionCategoryId || undefined,
  importance: question?.importance || WttpQuestionImportance.Medium,
  isActive: question?.isActive ?? true,
  fileAssets: !!question?.fileAssets?.length
    ? mapFileAssetsToForm(question.fileAssets)
    : [],
  parentId: question?.parentId || undefined,
});

export const QuestionForm = ({
  onFinish,
  question,
  refetchQueries,
  isClone,
  onClose,
  title,
}: QuestionFormProps) => {
  const { t } = useTranslation();

  const [createWttpQuestion, { loading: loadingCreate }] = useMutation(
    CreateWttpQuestionDocument,
    {
      refetchQueries,
    },
  );

  const [updateWttpQuestion, { loading: loadingUpdate }] = useMutation(
    UpdateWttpQuestionDocument,
    {
      refetchQueries,
    },
  );

  const loading = loadingCreate || loadingUpdate;

  const [displayCancelConfirmation, setDisplayCancelConfirmation] =
    useState(false);
  const isCloned = !!question?.parentId || isClone;

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, defaultValues, isDirty },
    reset,
  } = useForm<Form>({
    defaultValues: getFormData(question),
  });

  useEffect(() => {
    reset(getFormData(question));
  }, [question, reset]);

  const onCancel = () => {
    if (isDirty) {
      setDisplayCancelConfirmation(true);
    } else {
      onClose?.();
    }
  };

  const onSubmit = async (form: Form) => {
    const { fileAssets, ...rest } = form;
    const handledFileAssets = mapFileAssetsToRequest(fileAssets);

    const formData = {
      ...rest,
      fileAssets: handledFileAssets,
    };

    if (question?.id) {
      await updateWttpQuestion({
        variables: {
          id: question.id,
          data: formData,
        },
      });
    } else {
      await createWttpQuestion({
        variables: {
          data: formData,
        },
      });
    }

    onFinish?.();
  };

  const titleElement = title || t('wttp.question.form.create-question');

  return (
    <PageModal open onClose={onCancel} title={titleElement}>
      <div className={styles.wrapper}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.fieldsWrapper}>
            <Controller
              name="title"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  label={t('wttp.question.form.question-name')}
                  placeholder={t(
                    'wttp.question.form.question-name-placeholder',
                  )}
                  error={!!errors.title}
                  {...field}
                  ref={undefined}
                  inputRef={field.ref}
                  value={undefined}
                  defaultValue={field.value}
                  disabled={isCloned}
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <TextEditor
                  label={t('wttp.question.form.question-description')}
                  {...field}
                  defaultValue={field.value}
                  disabled={isCloned}
                />
              )}
            />
            <div className={styles.row}>
              <Typography variant="body2">Category name</Typography>
              <Controller
                name="questionCategoryId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <CategoriesField
                    className={styles.select}
                    field={field}
                    hasError={!!errors.questionCategoryId}
                    disabled={isCloned}
                  />
                )}
              />
            </div>
            <div className={styles.row}>
              <Typography variant="body2">Importance</Typography>
              <Controller
                name="importance"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <ImportanceField
                    className={styles.select}
                    field={field}
                    hasError={!!errors.importance}
                    disabled={isCloned}
                  />
                )}
              />
            </div>
            <div>
              <Typography variant="body2" className="mb-2">
                {t('wttp.question.form.troubleshooting-guide')}
              </Typography>
              <Controller
                name="fileAssets"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <AttachmentsField
                    key={defaultValues?.fileAssets?.length} // Rerender when form default value is changed
                    value={field.value}
                    onChange={(files) => {
                      return setValue(field.name, files);
                    }}
                  />
                )}
              />
            </div>
          </div>
          <Delimiter className="mt-6" />
          <BottomNavigation>
            <Button
              className="mr-2"
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              disabled={loading}
            >
              {t('wttp.question.form.submit-button')}
            </Button>
            <Button variant="outlined" color="primary" onClick={onCancel}>
              {t('common.close')}
            </Button>
          </BottomNavigation>
        </form>
      </div>
      <FormDirtyModal
        open={displayCancelConfirmation}
        onClose={() => setDisplayCancelConfirmation(false)}
        onCloseWithoutSaving={() => onClose?.()}
      />
    </PageModal>
  );
};
